/**
 * src/pages/admin/AdminTickets.js
 * 
 * Admin Support Tickets Management page.
 * View all user tickets, filter by status, reply, and update status.
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  TicketCheck,
  Search,
  MessageSquare,
  Clock,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Send,
  ChevronDown,
  ChevronUp,
  User,
  Mail,
} from "lucide-react";
import * as adminService from "../../services/adminService";
import { useToast } from "../../contexts/ToastContext";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { useAdmin } from "../../hooks/useAdmin";

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "open", label: "Open" },
  { id: "new", label: "New" },
  { id: "pending", label: "Pending" },
  { id: "in_progress", label: "In Progress" },
  { id: "resolved", label: "Resolved" },
  { id: "closed", label: "Closed" },
];

const STATUS_COLORS = {
  open: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  new: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  in_progress: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
  resolved: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  closed: "bg-gray-100 text-gray-600 dark:bg-neutral-700 dark:text-gray-400",
};

const STATUS_ICONS = {
  open: AlertCircle,
  new: AlertCircle,
  pending: Clock,
  in_progress: MessageSquare,
  resolved: CheckCircle2,
  closed: XCircle,
};

function formatTime(ts) {
  if (!ts) return "";
  const date = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
}

// ─── Ticket Card ────────────────────────────────────────────────
function TicketCard({ ticket, onStatusChange, onReply, adminUser }) {
  const [expanded, setExpanded] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [optimisticStatus, setOptimisticStatus] = useState(ticket.status || "open");
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    loading: false,
    action: null,
  });
  const toast = useToast();

  useEffect(() => {
    setOptimisticStatus(ticket.status || "open");
  }, [ticket.status]);

  const status = optimisticStatus;
  const StatusIcon = STATUS_ICONS[status] || AlertCircle;
  const statusColor = STATUS_COLORS[status] || STATUS_COLORS.open;

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setConfirmDialog({
      open: true,
      title: "Send Reply",
      message: "Send this reply to the user now?",
      loading: false,
      action: async () => {
        setReplying(true);
        try {
          await adminService.withAdminRetry(() => adminService.replyToTicket(adminUser, ticket.id, replyText.trim()));
          toast.success("Reply sent");
          setReplyText("");
          setShowReplyBox(false);
          onReply?.();
        } finally {
          setReplying(false);
        }
      },
    });
  };

  const handleStatusChange = async (newStatus) => {
    setConfirmDialog({
      open: true,
      title: "Update Ticket Status",
      message: `Set ticket status to ${newStatus.replace("_", " ")}?`,
      loading: false,
      action: async () => {
        const prevStatus = optimisticStatus;
        setOptimisticStatus(newStatus);
        try {
          await adminService.withAdminRetry(() => adminService.updateTicketStatus(adminUser, ticket.id, newStatus));
          toast.success(`Ticket marked as ${newStatus}`);
          onStatusChange?.();
        } catch (err) {
          setOptimisticStatus(prevStatus);
          throw err;
        }
      },
    });
  };

  const runConfirmAction = async () => {
    if (!confirmDialog.action) return;
    setConfirmDialog((prev) => ({ ...prev, loading: true }));
    try {
      await confirmDialog.action();
      setConfirmDialog({ open: false, title: "", message: "", loading: false, action: null });
    } catch (err) {
      toast.error(`Failed: ${err?.message || "Action failed"}`);
      setConfirmDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 overflow-hidden">
      {/* Header */}
      <div
        className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-750 transition"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${statusColor.split(' ').slice(0, 2).join(' ')}`}>
            <StatusIcon className={`w-4 h-4 ${statusColor.split(' ').slice(2).join(' ')}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white truncate">
                {ticket.subject || "Support Request"}
              </h3>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${statusColor}`}>
                {status.replace("_", " ")}
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
              {ticket.message || ticket.description || "No description"}
            </p>
            <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {ticket.userEmail || ticket.email || "Unknown"}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatTime(ticket.createdAt)}
              </span>
              {ticket.replies?.length > 0 && (
                <span className="flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" />
                  {ticket.replies.length} replies
                </span>
              )}
            </div>
          </div>
          <div className="flex-shrink-0">
            {expanded ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-neutral-700">
          {/* Full message */}
          <div className="p-4 bg-gray-50 dark:bg-neutral-900/50">
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {ticket.message || ticket.description || "No description provided."}
            </p>
            {ticket.category && (
              <p className="text-xs text-gray-500 mt-2">
                Category: <span className="font-medium">{ticket.category}</span>
              </p>
            )}
          </div>

          {/* Replies */}
          {ticket.replies?.length > 0 && (
            <div className="p-4 space-y-3">
              <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Conversation
              </h4>
              {ticket.replies.map((reply, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded-lg ${
                    reply.isAdmin
                      ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                      : "bg-gray-50 dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      {reply.isAdmin ? `Admin (${reply.sentByEmail || "admin"})` : "User"}
                    </span>
                    <span className="text-xs text-gray-400">
                      {reply.createdAt ? new Date(reply.createdAt).toLocaleString() : ""}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {reply.message}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="p-4 border-t border-gray-200 dark:border-neutral-700 space-y-3">
            {/* Status Actions */}
            <div className="flex flex-wrap gap-2">
              {status !== "in_progress" && (
                <button
                  onClick={() => handleStatusChange("in_progress")}
                  className="px-3 py-1.5 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 text-xs font-medium hover:bg-cyan-200 dark:hover:bg-cyan-900/50 transition"
                >
                  Mark In Progress
                </button>
              )}
              {status !== "resolved" && (
                <button
                  onClick={() => handleStatusChange("resolved")}
                  className="px-3 py-1.5 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium hover:bg-green-200 dark:hover:bg-green-900/50 transition"
                >
                  Mark Resolved
                </button>
              )}
              {status !== "closed" && (
                <button
                  onClick={() => handleStatusChange("closed")}
                  className="px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-400 text-xs font-medium hover:bg-gray-200 dark:hover:bg-neutral-600 transition"
                >
                  Close Ticket
                </button>
              )}
              <button
                onClick={() => setShowReplyBox(!showReplyBox)}
                className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition flex items-center gap-1"
              >
                <MessageSquare className="w-3 h-3" />
                Reply
              </button>
            </div>

            {/* Reply Box */}
            {showReplyBox && (
              <div className="flex gap-2">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your reply…"
                  rows={3}
                  className="flex-1 px-3 py-2 border border-gray-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 outline-none resize-none"
                />
                <button
                  onClick={handleReply}
                  disabled={replying || !replyText.trim()}
                  className="self-end px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 dark:disabled:bg-neutral-600 text-white font-medium rounded-lg transition disabled:cursor-not-allowed flex items-center gap-1"
                >
                  {replying ? (
                    <div className="orrith-loader orrith-loader-xs orrith-loader-on-dark" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        loading={confirmDialog.loading}
        confirmLabel="Confirm"
        onConfirm={runConfirmAction}
        onCancel={() => {
          if (confirmDialog.loading) return;
          setConfirmDialog({ open: false, title: "", message: "", loading: false, action: null });
        }}
      />
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────
export default function AdminTickets() {
  const { user: adminUser } = useAdmin();
  const [tickets, setTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const unsub = adminService.listSupportTickets((data) => {
      setTickets(data);
    }, statusFilter);
    return () => unsub();
  }, [statusFilter]);

  // Filter by search
  const filtered = tickets.filter((t) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (t.subject || "").toLowerCase().includes(q) ||
      (t.message || "").toLowerCase().includes(q) ||
      (t.userEmail || "").toLowerCase().includes(q) ||
      (t.email || "").toLowerCase().includes(q)
    );
  });

  // Counts
  const counts = {};
  tickets.forEach((t) => {
    const s = t.status || "open";
    counts[s] = (counts[s] || 0) + 1;
  });

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 h-14">
            <TicketCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              Support Tickets
            </h1>
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
              {tickets.length} total
            </span>
          </div>

          {/* Search + Filters */}
          <div className="pb-3 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tickets…"
                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-sm focus:ring-2 focus:ring-green-500 outline-none transition"
              />
            </div>
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setStatusFilter(f.id)}
                  className={`
                    px-3 py-1.5 rounded-full text-xs font-medium border transition whitespace-nowrap
                    ${statusFilter === f.id
                      ? 'bg-green-600 text-white border-green-600'
                      : 'border-gray-200 dark:border-neutral-600 text-gray-600 dark:text-gray-300 hover:border-green-500'
                    }
                  `}
                >
                  {f.label}
                  {f.id !== "all" && counts[f.id] ? ` (${counts[f.id]})` : ""}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 pb-24 space-y-3">
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <TicketCheck className="w-12 h-12 text-gray-300 dark:text-neutral-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {searchQuery ? "No tickets match your search" : "No tickets found"}
            </p>
          </div>
        ) : (
          filtered.map((ticket) => (
            <TicketCard key={ticket.id} ticket={ticket} adminUser={adminUser} />
          ))
        )}
      </div>
    </div>
  );
}
